<template>
    <div class="budget-item-grid">
        <MDBModal
            :id="'editViewModal' + heading"
            tabindex="-1"
            :labelledby="'editViewModal' + heading"
            v-model="show_modal"
            size="lg"
            :style="{ display: 'block' }"
            centered
            @hidden="onModalClosed()"
        >
            <MDBModalHeader>
                <MDBModalTitle>Edit</MDBModalTitle>
            </MDBModalHeader>
            <MDBModalBody>
                <MDBRow class="mb-4">
                <MDBCol>
                    <MDBDatepicker
                        v-model="selected_transaction.v3_date"
                        label="Select a date"
                        format="YYYY-MM-DD"
                        placeholder="YYYY-MM-DD"
                        class="mb-3"
                    />
                    <MDBInput
                        v-model="selected_transaction.v3_name"
                        label="Name"
                        class="mb-3"
                    />
                    <MDBInput
                        v-model="selected_transaction.v3_amount"
                        label="Amount"
                        class="mb-3"
                        type="number"
                    />
                    <MDBSelect 
                        class="mb-3" 
                        v-model:selected="selected_transaction.v3_category" 
                        v-model:options="static_categories" 
                        label="Category" 
                    />
                </MDBCol>
                </MDBRow> 
                <MDBRow>
                    <MDBBtn color="secondary" class="me-2" @click="cancelEdit()">Cancel</MDBBtn>
                    <MDBBtn color="primary" class="me-2" @click="acceptEdit()">Save</MDBBtn>
                    <MDBBtn color="danger" @click="deleteTransaction(selected_transaction); closeModal();">Delete</MDBBtn>
                </MDBRow> 
            </MDBModalBody>
        </MDBModal>

        <div style="display: flex; flex-direction: column; height: 100%">
            <div style="width: 100%; flex: 1 1 auto;">
                <ag-grid-vue
                    :style="{ }"
                    :domLayout="'autoHeight'"
                    class="ag-theme-alpine"
                    :defaultColDef="defaultColDef"
                    :columnDefs="columnDefs"
                    :rowData="griddata"
                    :rowSelection="rowSelection"
                    :getRowClass="getRowClass"
                    @gridReady="onGridReady"
                    @cellValueChanged="handleCellValueChanged"
                    :overlayLoadingTemplate="overlayLoadingTemplate"
                    @selection-changed="onSelectionChanged"
                    :loading="loading"
                    @rowDataUpdated="onRowDataUpdated"
                    @gridSizeChanged="onGridSizeChanged"
                    :rowHeight="rowHeight"
                    @rowDoubleClicked="onRowDoubleClicked"
                    @filterChanged="calculateHeightAndTotal"
                    :pinnedTopRowData="pinnedTopRowData"
                >
                </ag-grid-vue> 
            </div>
            </div> 
        </div>
</template>

<script>
    import "ag-grid-community/styles/ag-grid.css";
    import "ag-grid-community/styles/ag-theme-alpine.css";
    import { AgGridVue } from "ag-grid-vue3";
    import { reactive, ref } from "vue";
    import { patch } from '../common/api';
    import { MDBAccordion, MDBAccordionItem, MDBBtn, MDBModal, MDBModalHeader, MDBModalTitle, MDBModalBody, MDBInput, MDBSelect, MDBDatepicker, MDBRow, MDBCol } from 'mdb-vue-ui-kit';

let small = 600;
let medium = 800;

// Category icon mapping
const categoryIcons = {
    'gym_memberships': 'fa-dumbbell',
    'rent': 'fa-home',
    'daily_travel': 'fa-subway',
    'health_and_fitness': 'fa-heartbeat',
    'car_payment': 'fa-car',
    'utilities': 'fa-lightbulb',
    'steep_hollow': 'fa-tree',
    'subscriptions': 'fa-podcast',
    'zogchow': 'fa-utensils',
    'car_insurance': 'fa-shield-alt',
    'clothing': 'fa-tshirt',
    'discretionary': 'fa-store',
    'car_maintenance': 'fa-wrench',
    'other': 'fa-folder',
    'entertainment': 'fa-mountain',
    'groceries': 'fa-carrot'
};

export default {
    
    name: "BudgetItemGrid",
    props: {
        expense_categories: Array,
        data: Array,
        loading: Boolean,
        heading: String,
        selected_category: Object,
        month: Number,
        year: Number,
        type: String,
    },
    components: {
        AgGridVue,
        MDBBtn,
        MDBModal,
        MDBModalHeader,
        MDBModalTitle,
        MDBModalBody,
        MDBInput,
        MDBSelect,
        MDBDatepicker,
        MDBRow,
        MDBCol,
        MDBAccordion,
        MDBAccordionItem
    },
    emits: ['transaction-selected', 'transaction-edited', 'transaction-deleted', 'transaction-added'],

    data(props) {
        let rowHeight = 84;
        //let rowHeight = window.innerWidth <= small ? 84 : 42;
        //let open = props.type === 'expense' || props.type === 'transfers'
        //let height = rowHeight * (testData.length + 3) + 10;
        return {
            gridtype: props.type,
            transaction_before_edit: reactive({}),
            selected_transaction: reactive({}),
            show_modal: false,
            pinnedTopRowData: [{'v3_amount': 0.00}],
            defaultColDef: {
                sortable: true,
                filter: true,
                editable: (params) => !params.node.isRowPinned() && window.innerWidth > medium,
                wrapText: true,
                autoHeight: false,
                suppressMovable: true,
                lockPosition: "left",
                resizable: window.innerWidth > medium,
                cellStyle: params => {
                    let style = {
                        whiteSpace: "pre-line",
                        display: "flex",
                        alignItems: "center",
                        height: "100%"
                    };
                    if (params.data.v3_deleted) {
                        style.color = 'red';
                    }
                    // Keep italic style for pending items even when selected
                    if (params.data.pending && !params.data.v3_deleted) {
                        style.fontStyle = 'italic';
                    }
                    return style;
                },
            },
            static_categories: [],
            rowHeight: rowHeight,
            griddata: [],
            gridApi: ref(null),
            overlayLoadingTemplate: null,
            shiftDown: null,
            deleteDown: null,
            columnDefs: this.getColumnDefs(),
            rowSelection: {
                mode: 'multiRow',
                checkboxes: false,
                headerCheckbox: false,
                enableClickSelection: true,
            },
            edit_accepted: false,
        }
    },
    methods: {
        onModalClosed() {
            if (this.edit_accepted) {
                console.log("accepting edit, saving changes");
                this.editTransaction(this.selected_transaction);
                this.edit_accepted = false;
            }
            else {
                console.log("canceling edit, restoring previous state");
                this.selected_transaction = JSON.parse(JSON.stringify(this.transaction_before_edit));
                this.griddata.forEach((item, idx) => {
                    if (item.date_transaction_id === this.selected_transaction.date_transaction_id) {
                        this.griddata[idx] = this.selected_transaction;
                    }
                });
                this.gridApi.setGridOption("rowData", this.griddata);
                this.calculateHeightAndTotal(); 
            }
            this.edit_accepted = false;
        },
        cancelEdit() {
           this.edit_accepted = false;
           this.show_modal = false;
        },
        acceptEdit() {
            this.edit_accepted = true;
            this.show_modal = false;
        },
        handleChange(e) {
            console.log(e)
            console.log(this.selected_transaction.v3_category)
        },
        onRowDoubleClicked(params) {
            this.transaction_before_edit = JSON.parse(JSON.stringify(params.data));
            console.log("transaction_before_edit:", this.transaction_before_edit);
            this.selected_transaction = params.data;
            this.static_categories?.forEach(category => {
                category.selected = false;
                if (category.value === this.selected_transaction.v3_category) {
                    category.selected = true;
                }
            });
            if (window.innerWidth <= medium) {
                console.log(this.selected_transaction)
                this.show_modal = true;
            }
        },
        onGridSizeChanged(params) {
            if (params.clientWidth <= medium) {
                 this.gridApi.setColumnsVisible(['v3_account_name'], false);
            }
            else {
                this.gridApi.setColumnsVisible(['v3_account_name'], true);
            }
            if (params.clientWidth <= small) {
                this.gridApi.setColumnsVisible(['v3_category'], false);
            }
            else {
                this.gridApi.setColumnsVisible(['v3_category'], true);
            }
            //this.calculateHeightAndTotal();
        },
        getRowClass(params) {
            // Apply pending-transaction class to entire row
            if (params.data.pending && !params.data.v3_deleted) {
                return 'pending-transaction';
            }
            return '';
        },
        getName(params) {
            return params.data.v3_name ? params.data.v3_name : params.data.name
        },
        formatName(params) {
            return params.value || "";
        },
        getCategoryIcon(categoryName) {
            return categoryIcons[categoryName] || 'fa-tag';
        },
        nameCellRenderer(params) {
            // Don't render icon for pinned rows (totals)
            if (params.node.isRowPinned()) {
                return params.value || '';
            }

            if (window.innerWidth <= small) {
                const categoryIcon = categoryIcons[params.data.v3_category] || 'fa-tag';
                const category = this.static_categories?.find(x => x.value === params.data.v3_category);
                const categoryName = category ? category.text : '';
                const textValue = params.value || '';

                return `
                    <div style="display: flex; align-items: center; gap: 12px; width: 100%; height: 100%; padding: 2px 0;">
                        <i class="fas ${categoryIcon}" style="color: #666; font-size: 16px; flex-shrink: 0;" title="${categoryName}"></i>
                        <span class="mobile-name-text">${textValue}</span>
                    </div>
                `;
            }
            return params.value || '';
        },
        formatAccount(params) {
            let display = params.value || "";
            if (display.length > 35) {
                display = display.slice(0, 33);
                display += '...';
            }
            return display;
        },
        formatAmount(params) {
            return '$' + Number(params.value).toFixed(2);
        },
        formatCategory(params) {
            return this.static_categories?.find(x => x.value === params.value)?.text;
        },
        formatDate(params) {
            return params.data.v3_date?.slice(-2, params.data.v3_date.length);
        },
        setDate(params) {
            console.log("setDate called with params:");
            console.log(params);
            if (!params.newValue) {
                return false;
            }
            let day = params.newValue
            if (day < 10) {
                day = '0' + day
            }
            params.data.v3_date = this.year + '-' + (this.month + 1) + '-' + day;
            return true;
        },
        amountGetter(params) {
            let amount;
            if (this.gridtype === 'income') {
                amount = 0 - params.data.v3_amount;
            } else {
                amount = params.data.v3_amount;
            }
            return amount;
        },
        amountSetter(params) {
            let value;
            if (this.gridtype === 'income') {
                value = Number(params.newValue) * -1;
            } else {
                value = Number(params.newValue);
            }
            params.data.v3_amount = value;
            return true;
        },
        getColumnDefs() {
            return [
                { 
                    colId: 'v3_date',
                    field: 'v3_date',
                    headerName: window.innerWidth < 600 ? '' : 'Date',
                    valueFormatter: this.formatDate,
                    valueGetter: (params) => params.data.v3_date, 
                    //valueSetter: this.setDate,
                    sortingOrder: ["desc", "asc"], 
                    flex: 1,
                    cellEditor: 'agNumberCellEditor',
                    minWidth: 55,
                },
                {
                    colId: 'v3_name',
                    field: 'v3_name',
                    headerName: "Name",
                    valueGetter: this.getName,
                    cellRenderer: window.innerWidth <= small ? this.nameCellRenderer : undefined,
                    flex: 6,
                    cellStyle: params => {
                        let style = {
                            display: "flex",
                            alignItems: "center",
                            height: "100%",
                        };

                        // Apply default styles from defaultColDef
                        if (params.data.v3_deleted) {
                            style.color = 'red';
                        }
                        // Keep italic style for pending items even when selected
                        if (params.data.pending && !params.data.v3_deleted) {
                            style.fontStyle = 'italic';
                        }

                        if (window.innerWidth <= small) {
                            style.overflow = 'visible';
                        } else {
                            style.whiteSpace = 'pre-line';
                        }

                        return style;
                    }
                },
                { 
                    colId: 'v3_amount',
                    field: 'v3_amount',
                    headerName: 'Amount',
                    valueFormatter: this.formatAmount,
                    valueGetter: this.amountGetter,
                    valueSetter: this.amountSetter,
                    cellDataType: 'number',
                    flex: 2, 
                    cellEditor: 'agNumberCellEditor',
                    minWidth: 100,
                },
                { 
                    colId: 'v3_category',
                    field: 'v3_category', 
                    headerName: 'Category',
                    cellEditor: 'agSelectCellEditor',
                    cellEditorParams: () =>{ 
                        return { values: this.static_categories?.map(x => x.value) };                
                    },
                    valueFormatter: this.formatCategory,
                    valueGetter: (params) => params.data.v3_category,
                    flex: 2, 
                    singleClickEdit: true,
                },
                { 
                    colId: 'v3_account_name',
                    field: 'v3_account_name',
                    headerName: 'Account',
                    flex: 2, 
                    valueFormatter: this.formatAccount,
                    wrapText: false,
                    editable: false,
                }
            ]
        },
        onSelectionChanged() {
            const selectedRows = this.gridApi.getSelectedRows();
            if (selectedRows.length == 1) {
                this.selected_transaction = selectedRows[0];
                this.$emit('transaction-selected', this.selected_transaction);
            }
            // Refresh cells to update styling when selection changes
            this.gridApi.refreshCells({ force: true });
        },
        onRowDataUpdated() {
            this.calculateHeightAndTotal();
        },
        editTransaction(transaction) {
            this.calculateHeightAndTotal(); 
            console.log("emitting transaction-edited");
            console.log(transaction)
            this.$emit('transaction-edited', transaction);

            patch('transactions', transaction)
                .then(result => {
                    if (result.status == 401) {
                        localStorage.removeItem('token');
                        this.$router.push('/login');
                        throw new Error("Unauthorized");
                    }
                    return result;
                })
                .then(result => {
                    if (!result.ok) {
                        throw new Error("Failed to update, check console");
                    }
                    return result;
                })
                .then(() => {
                    this.calculateHeightAndTotal();
                })
                .catch(err => { alert("Failed to update, check console"); console.log(err); });
        
        },
        handleCellValueChanged(params) {
            params.node.data.v3_amount = Number(params.node.data.v3_amount);
            this.editTransaction(params.node.data);
        },
        deleteTransaction(transaction) {
            console.log("deleting transaction:")
            console.log(transaction)
            transaction.v3_deleted = true;            
            patch('transactions', { date_transaction_id: transaction['date_transaction_id'], v3_deleted: true })
                .then(result => {
                    if (result.status == 401) {
                        localStorage.removeItem('token');
                        this.$router.push('/login');
                        throw new Error("Unauthorized");
                    }
                    return result;
                })
                .then(result => {
                    if (!result.ok) {
                        throw new Error("Failed to update, check console");
                    }
                    return result;
                })
                .then(data => {
                    console.log("emitting transaction-deleted")
                    this.$emit('transaction-deleted', data);
                })
                .catch(err => { alert("Failed to delete, check console"); console.log(err) });
        },
        onGridReady(params) {
            this.gridApi = params.api;
            this.gridApi.setColumnGroupOpened('account_group', false)
        },
        generatePinnedTopData() {
            //console.log(this.heading + " generating pinned top data:");
            // generate a row-data with null values

            if (!this.gridApi) {
                return;
            }
            let result = this.calculatePinnedTopData();
            //console.log(this.heading + " generated pinned top data: ");
            ///console.log(result);
            return result;
        },
        calculatePinnedTopData() {

            //**list of columns for aggregation**
            let target = { 'v3_amount': 0.00 };
            let columnsWithAggregation = ['v3_amount'];
            columnsWithAggregation.forEach(element => {
                let total = 0;
                this.gridApi.forEachNodeAfterFilter((rowNode) => {
                    if (rowNode.data[element]) {
                        total += Number(rowNode.data[element]);
                    }
                });
                target[element] = total;
            })
            return target;
        },
        calculateHeightAndTotal() {
            if (this.heading == "spending") {
                console.log("Calculating height and total for spending grid");
            }
            //this.height = (rowHeight * (this.griddata.length + 3) + 10).toString() + "px";
            let pinnedTopData = this.generatePinnedTopData();
            //this.pinnedTopRowData = [pinnedTopData];
            this.gridApi?.setGridOption('pinnedTopRowData', [pinnedTopData]);
        },
    },
    watch: {
        data(newData) {
            console.log("data changed in BudgetItemGrid");
            this.griddata = newData.sort((a, b) => {
                return new Date(b.v3_date) - new Date(a.v3_date);
            });
            if (this.static_categories.length === 0) {
                return;
            }
            this.calculateHeightAndTotal();
        },
        expense_categories(new_expense_categories) {
            console.log("expense_categories changed in BudgetItemGrid");
            //this.columnDefs = this.getColumnDefs();
            //deep copy to avoid mutating prop
            this.static_categories = JSON.parse(JSON.stringify(new_expense_categories));
            //this.calculateHeightAndTotal();
        },
        selected_category(newCategory, oldCategory) {
            console.log("Selected category changed in BudgetItemGrid");
            if (newCategory != null && newCategory.name) {
                if (newCategory.name === oldCategory?.name) {
                    console.log("Category is the same as before, skipping");
                    return;
                }
                this.gridApi.setFilterModel({
                    v3_category: {
                        type: 'equals',
                        filter: newCategory.name,
                    }
                });
            }
            else {
                this.gridApi.setFilterModel(null);
            }
        }
    },
    updated() {
    },
    created() {

        this.overlayLoadingTemplate =
            '<span class="ag-overlay-loading-center">Getting transactions...</span>';
        window.addEventListener('keydown', (e) => {

            if (e.key == 'Delete') {
                this.deleteDown = true;
            }
            else if (e.key == 'Shift') {
                this.shiftDown = true;
            }

            if (this.deleteDown && this.shiftDown && this.selected_transaction['date_transaction_id']) {
                e.preventDefault();
                e.stopPropagation();
                this.deleteTransaction(this.selected_transaction)
            }

        });

        window.addEventListener('keyup', (e) => {

            if (e.key == 'Delete') {
                this.deleteDown = false;
            }
            else if (e.key == 'Shift') {
                this.shiftDown = false;
            }
            
        });
    },

}
</script>

<style scoped>
    .budget-item-grid {
        margin-bottom: 40px;
        margin-top: 10px;
    }
    .budget-item-grid-title {
        margin-left: 50px;
    }
</style>

<style>
    @media (max-width: 600px) {
        .mobile-name-text {
            flex: 1;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            word-break: break-word;
            line-height: 1.7;
            width: 100%;
            padding-right: 2px;
        }
    }

    /* Pending transaction background - only when NOT selected */
    .ag-row.pending-transaction {
        background-color: #fcf3d4;
    }

    /* Remove pending background when row is selected */
    .ag-row.ag-row-selected.pending-transaction {
        background-color: transparent;
    }
</style>